import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import './MatchHistoryPage.css';
import PageButton from '../../elements/PageButton/PageButton';
import Pagination from '../../elements/Pagination/Pagination';
import MatchHistoryItem from '../../MatchHistoryItem/MatchHistoryItem';
import {usePageAnalytics} from '../../../hooks/analytics/usePageAnalytics';
import {API_BASE} from '../../../utils/apiConfig';
import {addClassNameIfTrue} from "../../../utils/other/addClassNameIfTrue";
import {MENU_SOUND_MANAGER} from "../../../scenes/MenuSoundManager";
import {MATCH_TYPE_FILTER_OPTIONS, MATCH_TYPE_FILTERS} from "../../../constants/matchTypeFilters";
import FloatingBackButton from "../../elements/FloatingBackButton/FloatingBackButton";
import {RANKED_STAR_ICON_PATHS, RANKED_STAR_KINDS} from "../../../utils/matches/rankedStarIcon";

const PAGE_ID = 'MatchHistoryPage';
const PAGE_SIZE = 10;

function MatchHistoryPage({isAllMatches, userId, userName, onBack, onMatchClick}) {
  const [matches, setMatches] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cache, setCache] = useState(new Map());
  // Фильтры по ботам — «Не боты» и «2+ живых» — только админам: они отбирают
  // матчи по скрытым признакам ботов (сервер не-админу их и не применит, см.
  // getMatchListFilterConditions). Общий список всех матчей и так админский
  const isViewerAdmin = useSelector((state) => Boolean(state.private.auth.isAdmin || state.private.auth.isSuperAdmin));
  const areBotFiltersShown = isAllMatches || isViewerAdmin;
  // В общем списке по умолчанию скрываем чисто ботовые матчи — интересны
  // локальные и сетевые с хотя бы одним реальным игроком. История игрока по
  // умолчанию полная, как и без фильтров
  const [isRealPlayersOnly, setIsRealPlayersOnly] = useState(Boolean(isAllMatches));
  // Только сетевые матчи с несколькими живыми игроками — у рейтинговых из
  // них в плашке синяя звезда (см. getRankedStarKind)
  const [isMultiplayerOnly, setIsMultiplayerOnly] = useState(false);
  // Фильтр по типу матча — всем, и в истории игрока тоже. Выборка идет на
  // сервере: список постраничный, и фильтрация уже полученной страницы
  // разъехалась бы с числом страниц
  const [matchTypeFilter, setMatchTypeFilter] = useState(MATCH_TYPE_FILTERS.ALL);
  const isRealPlayersOnlyApplied = areBotFiltersShown && isRealPlayersOnly;
  const isMultiplayerOnlyApplied = areBotFiltersShown && isMultiplayerOnly;
  const isAnyFilterApplied = matchTypeFilter !== MATCH_TYPE_FILTERS.ALL
    || isMultiplayerOnlyApplied
    // В общем списке «Не боты» включен по умолчанию — это его обычный вид
    || (isRealPlayersOnlyApplied && !isAllMatches);

  usePageAnalytics('MatchHistoryPage');

  useEffect(() => {
    const fetchMatches = async () => {
      setLoading(true);
      setError(null);

      const filtersKey = `${isRealPlayersOnlyApplied ? 'real' : 'any'}_${isMultiplayerOnlyApplied ? 'multi' : 'any'}`
        + `_${matchTypeFilter}`;
      const cacheKey = isAllMatches ? `all_${filtersKey}_${page}` : `${userId}_${filtersKey}_${page}`;
      const cached = cache.get(cacheKey);
      if (cached) {
        setMatches(cached.matches);
        setTotalPages(cached.totalPages);
        setLoading(false);
        return;
      }

      try {
        const filterParams = `${isRealPlayersOnlyApplied ? '&realOnly=1' : ''}`
          + `${isMultiplayerOnlyApplied ? '&multiplayerOnly=1' : ''}`
          + `${matchTypeFilter === MATCH_TYPE_FILTERS.ALL ? '' : `&type=${matchTypeFilter}`}`;
        const url = isAllMatches
          ? `${API_BASE}/admin/matches?limit=${PAGE_SIZE}&page=${page}${filterParams}`
          : `${API_BASE}/users/${userId}/matches?limit=${PAGE_SIZE}&page=${page}${filterParams}`;
        const res = await fetch(url, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error('Failed to fetch match history');
        const data = await res.json();
        setMatches(data.matches || []);
        setTotalPages(data.totalPages || 1);
        setCache((prev) => {
          const next = new Map(prev);
          next.set(cacheKey, data);
          return next;
        });
      } catch (e) {
        console.error('Match history error', e);
        setError('Не удалось загрузить историю матчей');
      } finally {
        setLoading(false);
      }
    };

    fetchMatches();
  }, [page, cache, userId, isAllMatches, isRealPlayersOnlyApplied, isMultiplayerOnlyApplied, matchTypeFilter]);

  const handlePageChange = (newPage) => {
    MENU_SOUND_MANAGER.playMenuButtonClick();
    setPage(newPage);
  };

  const handleRealPlayersOnlyToggle = () => {
    MENU_SOUND_MANAGER.playMenuButtonClick();
    // Наборы страниц у двух режимов разные — оставаться на текущем номере нельзя
    setPage(1);
    setIsRealPlayersOnly((prev) => !prev);
  };

  const handleMultiplayerOnlyToggle = () => {
    MENU_SOUND_MANAGER.playMenuButtonClick();
    setPage(1);
    setIsMultiplayerOnly((prev) => !prev);
  };

  const handleMatchTypeFilterClick = (typeId) => {
    if (typeId === matchTypeFilter) {
      return;
    }
    // По той же причине, что и выше: у каждого типа своя разбивка на страницы
    setPage(1);
    setMatchTypeFilter(typeId);
  };

  let title;
  if (isAllMatches) {
    title = 'Все матчи';
  } else {
    title = 'История матчей';
  }

  const isUserNameShown = userName && !isAllMatches

  return (
    <>
      {isAllMatches && (
        <FloatingBackButton onBack={onBack} pageId={PAGE_ID}/>
      )}
      <h1
        className={`admin-user-profile-title ${addClassNameIfTrue(!isUserNameShown, 'history-page-title-margin-bottom')}`}
      >
        {title}
      </h1>

      {isUserNameShown && (
        <h1 className="admin-user-profile-name">{userName}</h1>
      )}

      {areBotFiltersShown && (
        <div className="match-history-page-filters">
          <label className="match-history-page-filter">
            <input
              type="checkbox"
              checked={isRealPlayersOnly}
              onChange={handleRealPlayersOnlyToggle}
            />
            <span>Не боты</span>
          </label>
          <label
            className="match-history-page-filter"
            title="Сетевые матчи, где живых игроков больше одного"
          >
            <input
              type="checkbox"
              checked={isMultiplayerOnly}
              onChange={handleMultiplayerOnlyToggle}
            />
            <img
              className="match-history-page-filter-icon"
              src={RANKED_STAR_ICON_PATHS[RANKED_STAR_KINDS.MULTIPLAYER]}
              alt=""
              aria-hidden="true"
            />
            <span>2+ живых</span>
          </label>
        </div>
      )}

      <div className="match-history-page-types">
        {MATCH_TYPE_FILTER_OPTIONS.map((option) => (
          <PageButton
            key={option.id}
            className={`match-history-page-type-button${
              matchTypeFilter === option.id ? ' match-history-page-type-button_active' : ''
            }`}
            pageId={PAGE_ID}
            buttonId={`type_${option.id}`}
            onClick={() => handleMatchTypeFilterClick(option.id)}
          >
            {option.label}
          </PageButton>
        ))}
      </div>

      {loading && <p className="match-history-page-status">Загрузка...</p>}
      {error && <p className="match-history-page-status match-history-page-status_error">{error}</p>}

      {!loading && !error && (
        <>
          {matches.length === 0 ? (
            <p className="match-history-page-status">
              {isAnyFilterApplied ? 'Нет матчей с такими фильтрами' : 'Пока нет сыгранных матчей'}
            </p>
          ) : (
            <>
              <div className="match-history-page-list">
                {matches.map((match, index) => (
                  <MatchHistoryItem
                    key={match._id || match.matchId}
                    match={match}
                    userId={isAllMatches ? undefined : userId}
                    showLocalOwnerName={isAllMatches}
                    onClick={() => onMatchClick(match.matchId)}
                    pageId={PAGE_ID}
                    buttonId={`match_${index}`}
                  />
                ))}
              </div>
              <Pagination currentPage={page} totalPages={totalPages} onPageChange={handlePageChange}/>
            </>
          )}
        </>
      )}

      <div className="main-menu-buttons match-history-page-buttons">
        <PageButton onClick={onBack} pageId={PAGE_ID} buttonId="back">
          Назад
        </PageButton>
      </div>
    </>
  );
}

export default MatchHistoryPage;
